// Pure helpers for the Admin → Logs stream.
//
// They live outside `screens/admin-logs.tsx` because that file drags in the
// copilot panel, the API hooks and the user drawer — nothing a node-env vitest
// run can render. Here they are testable on their own.

export type StatusTone = 'ok' | 'warn' | 'bad';

const HTTP_VERBS = new Set(['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS']);

/** Split `PUT /campaigns/campaign_23e4…` into its parts.
 *
 *  Only when the leading token really is a verb: the frontend writes
 *  `METHOD /concrete/path` while the API writes the bare axum `MatchedPath`
 *  template (`/campaigns/{id}`), and a template must not lose its first
 *  segment to a split. */
export function splitRoute(route: string | null | undefined): { method: string | null; path: string } {
  const value = (route ?? '').trim();
  if (!value) return { method: null, path: '' };
  const gap = value.indexOf(' ');
  if (gap > 0) {
    const head = value.slice(0, gap);
    if (HTTP_VERBS.has(head)) return { method: head, path: value.slice(gap + 1).trim() };
  }
  return { method: null, path: value };
}

/** How loudly to render an HTTP status.
 *
 *  4xx is `warn`, not `bad` — a 409 conflict is the operator being told "not
 *  like that", and the line's own level says `warn`. Painting it the same red
 *  as a 500 trains people to ignore red. */
export function statusTone(status: number | null | undefined): StatusTone | null {
  if (typeof status !== 'number' || !Number.isFinite(status)) return null;
  if (status >= 500) return 'bad';
  if (status >= 400) return 'warn';
  return 'ok';
}

export interface LogRequestSummary {
  method: string | null;
  path: string;
  status: number | null;
  tone: StatusTone | null;
  /** Full, untruncated — the row shows an ellipsized path and hangs this in a
   *  `title`. Bounded at source: `route` is cut to 128 chars by
   *  `sanitize_app_log_line` before it is ever stored. */
  title: string;
}

export function logRequestSummary(
  log: { route?: string | null; http_status?: number | null } | null | undefined,
): LogRequestSummary | null {
  const route = log?.route ?? null;
  const status = typeof log?.http_status === 'number' ? log.http_status : null;
  if (!route && status === null) return null;
  const { method, path } = splitRoute(route);
  const title = [method, path, status === null ? null : `→ ${status}`]
    .filter(Boolean)
    .join(' ');
  return { method, path, status, tone: statusTone(status), title };
}

/** The identifier shape the server stores and will accept as a filter —
 *  mirrors `sanitize_identifier` in shared/src/redact.rs. Typing something
 *  outside it must not silently return an unfiltered stream. */
export function isErrorKind(value: string): boolean {
  return value.length > 0 && value.length <= 64 && /^[A-Za-z0-9._-]+$/.test(value);
}

export type StreamState = 'rows' | 'error' | 'loading' | 'empty' | 'empty-signature';

/** Which arm the stream should render.
 *
 *  `rows` wins over `error` on purpose: a failed poll must never blank a tail
 *  the operator is already reading. The banner reports the failure alongside. */
export function streamStateFor(input: {
  isError: boolean;
  isLoading: boolean;
  rowCount: number;
  hasSignature: boolean;
}): StreamState {
  if (input.rowCount > 0) return 'rows';
  if (input.isError) return 'error';
  if (input.isLoading) return 'loading';
  return input.hasSignature ? 'empty-signature' : 'empty';
}

export interface LogBreadcrumb {
  kind: string;
  msg: string;
}

/** A log line's `context_json`, narrowed for display. */
export interface LogContext {
  /** The last eight frontend breadcrumbs, oldest first. */
  breadcrumbs: LogBreadcrumb[];
  /** Every other structured field (e.g. `error`, `rule` from the batch relay),
   *  its value as display text. */
  fields: Array<[string, string]>;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const displayText = (value: unknown): string =>
  typeof value === 'string' ? value : value === undefined ? '' : JSON.stringify(value);

/** Parse a log line's `context_json` for the expanded row.
 *
 *  The column holds whatever JSON the producer sent — the browser's telemetry
 *  posts it, and the server sanitizes but does not reshape it — so nothing is
 *  assumed about its shape. `breadcrumbs` is rendered as a trail only when it
 *  is an array; anything else under that key is shown as a plain field. The
 *  screen used to call `.map` on any non-empty `breadcrumbs` and render each
 *  `kind`/`msg` as a React child, so one line carrying `"breadcrumbs": "x"` or
 *  an object `kind` threw while rendering and took down the whole Logs screen
 *  for as long as that line stayed in the window. */
export function parseLogContext(json: string | null | undefined): LogContext | null {
  if (!json) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (parsed === null) return null;
  if (!isRecord(parsed) && !Array.isArray(parsed)) {
    // A bare string or number: one field, not one row per character.
    return { breadcrumbs: [], fields: [['context', displayText(parsed)]] };
  }
  const trail = isRecord(parsed) && Array.isArray(parsed.breadcrumbs) ? parsed.breadcrumbs : null;
  const breadcrumbs = (trail ?? []).slice(-8).map((b: unknown): LogBreadcrumb => (
    isRecord(b) ? { kind: displayText(b.kind), msg: displayText(b.msg) } : { kind: '', msg: displayText(b) }
  ));
  const fields = Object.entries(parsed)
    .filter(([k]) => !(trail && k === 'breadcrumbs'))
    .map(([k, v]): [string, string] => [k, displayText(v)]);
  return { breadcrumbs, fields };
}
